import { useEffect, useRef, useState } from "react";

// Ports the Screen Wake Lock keep-alive from workout-pwa (index.html:269-304):
// a token guards against a stale in-flight request() clobbering a newer
// acquire/release, and a visibilitychange listener re-acquires the lock when
// Safari force-releases it on tab hide (Safari doesn't auto-restore it).
export function useWakeLock(active: boolean) {
  const [warn, setWarn] = useState(false);
  const wantedRef = useRef(false);
  const tokenRef = useRef(0);
  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    function acquire() {
      wantedRef.current = true;
      if (!navigator.wakeLock) {
        setWarn(true);
        return;
      }
      const token = ++tokenRef.current;
      navigator.wakeLock
        .request("screen")
        .then((sentinel) => {
          if (!wantedRef.current || token !== tokenRef.current) {
            sentinel.release().catch(() => {});
            return;
          }
          sentinelRef.current = sentinel;
          setWarn(false);
          sentinel.addEventListener("release", () => {
            if (sentinelRef.current === sentinel) sentinelRef.current = null;
          });
        })
        .catch(() => setWarn(true));
    }

    function release() {
      wantedRef.current = false;
      tokenRef.current += 1;
      if (sentinelRef.current) {
        sentinelRef.current.release().catch(() => {});
        sentinelRef.current = null;
      }
    }

    if (active) acquire();
    else release();

    function onVisibilityChange() {
      if (document.visibilityState === "visible" && wantedRef.current && !sentinelRef.current) {
        acquire();
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      release();
    };
  }, [active]);

  return { warn };
}
